import { capitalize } from 'util/strings'

export function isPlanLimitsExceeded(plan, usage) {
  const productFeatures = plan.features
  const featureLookup = Object.keys(productFeatures)
  const productUsage = usage
  return Object.keys(productUsage).some(key => {
    const usageValue = productUsage[key]
    // Eg custom_profile, rules
    if (
      featureLookup.includes(key) &&
      typeof usageValue === 'boolean' &&
      usageValue &&
      !productFeatures[key]
    ) {
      return true
    }

    // Eg teams
    if (
      featureLookup.includes(key) &&
      typeof usageValue === 'number' &&
      usageValue > 0 &&
      !productFeatures[key]
    ) {
      return true
    }

    // Eg max_channels, max_integrations, max_kbs
    const featureKey = `max${capitalize(key)}`
    if (
      featureLookup.includes(featureKey) &&
      typeof usageValue === 'number' &&
      productFeatures[featureKey] !== 'unlimited' &&
      usageValue > productFeatures[featureKey]
    ) {
      return true
    }
    return false
  })
}

export function isAllowedAccessToFeature(plan, feature) {
  if (!plan || !plan.features || !feature) return true
  return !!plan.features[feature]
}

export function isPlanSmaller(currentPlan, newPlan) {
  if (currentPlan.version !== newPlan.version) return false
  return currentPlan.monthlyPrice >= newPlan.monthlyPrice
}

export const buildLineItemFromPlan = (plan, billingCycle, quantity = 1) => {
  const { minimumQuantity, pricingModel } = plan

  let cyclePrice =
    billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice
  if (pricingModel !== 'flat') {
    cyclePrice *= Math.max(minimumQuantity, quantity)
  }

  return {
    ...plan,
    id: plan.planId,
    cyclePrice,
    itemPrice: billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice,
    minimumQuantity,
    quantity,
  }
}

export const buildLineItemsFromPlans = ({
  inboxPlanId,
  kbPlanId,
  billingCycle,
  plans,
  usage,
  provider,
}) => {
  const inboxPlan = plans.find(
    p => p.product === 'inbox' && p.planId === inboxPlanId
  )
  const kbPlan = plans.find(p => p.product === 'kb' && p.planId === kbPlanId)
  if (inboxPlan.pricingModel === 'flat') {
    return [buildLineItemFromPlan(inboxPlan, billingCycle, usage.inbox.users)]
  }

  const lineItems = [
    buildLineItemFromPlan(inboxPlan, billingCycle, usage.inbox.users),
  ]

  if (provider !== 'shopify') {
    lineItems.push(buildLineItemFromPlan(kbPlan, billingCycle, 1))
  }

  return lineItems
}

export const calculateSubscriptionFlatTotal = (planLineItems = []) => {
  return planLineItems
    .filter(r => r.pricingModel === 'flat')
    .reduce((total, row) => {
      // eslint-disable-next-line no-param-reassign
      total += row.cyclePrice
      return total
    }, 0)
}

export const calculateSubscriptionUsageTotal = (planLineItems = []) => {
  return planLineItems
    .filter(r => r.pricingModel === 'usage')
    .reduce((total, row) => {
      // eslint-disable-next-line no-param-reassign
      total += row.cyclePrice
      return total
    }, 0)
}

export const calculateSubscriptionMaxUsageAmount = (planLineItems = []) => {
  return calculateSubscriptionUsageTotal(planLineItems) * 100
}

const splitPlanIntoVersionAndPlan = plan => {
  if (!plan) return { version: 1, plan: '' }
  const matches = /v(\d+)-(.*)/g.exec(plan.value)
  if (!matches) return { version: 1, plan: plan.value }
  const [, version, planPart] = matches
  return {
    version: parseInt(version, 10),
    plan: planPart,
  }
}

export const isVersionUpgrade = (newPlan, currentPlan) => {
  const {
    version: newVersion,
    plan: newPlanPart,
  } = splitPlanIntoVersionAndPlan(newPlan)
  const {
    version: currentVersion,
    plan: currentPlanPart,
  } = splitPlanIntoVersionAndPlan(currentPlan)
  if (newVersion > currentVersion && newPlanPart === currentPlanPart)
    return true
  return false
}

export const getPlanSupportedCycles = (product, plan, plans) => {
  const planInfo = plans.find(p => p.product === product && p.value === plan)
  if (
    // If we cant find the plan info, assume it supports monthly and annual
    !planInfo ||
    // If the plan is completely free, then it supports both annual and monthly (kb-basic)
    (planInfo.monthly === 0 && planInfo.annual === 0) ||
    // If it has a month and annual price, then it supports both annual and monthly
    (planInfo.monthly !== 0 && planInfo.annual !== 0)
  )
    return 'all'
  if (planInfo.monthly === 0) return 'annual'
  return 'monthly'
}

// We need to change max_kbs for v17 plans on frontend
export const getMaxKbsForInboxPlanId = (inboxPlanId, originalMaxKbs) => {
  if (originalMaxKbs === undefined) {
    // eslint-disable-next-line no-console
    console.error('Must provide the original max_kbs', {
      max_kbs: originalMaxKbs,
    })
  }
  const countMap = {
    'v17-free': 0,
    'v17-standard': 1,
  }
  return countMap[inboxPlanId] === undefined
    ? originalMaxKbs
    : countMap[inboxPlanId]
}

export const isVersionSmaller = (version = '', targetVersion = '') => {
  const getVersionNumber = v => Number(v.split('v').filter(Boolean)[0])
  return getVersionNumber(version) < getVersionNumber(targetVersion)
}

export const isInboxPlanVersionSmallerThan17 = version =>
  version && isVersionSmaller(version, 'v17')

export const pricingIdToPlan = pricingId => {
  return pricingId.replace(/(kb-|inbox-|-annual)/g, '')
}

export const pricingIdToComponents = pricingId => {
  const parts = pricingId.split('-')
  const product = parts.shift()
  let cycle = 'monthly'

  if (parts[parts.length - 1] === 'annual') {
    cycle = parts.pop()
  }

  const planId = parts.join('-')

  return {
    product,
    planId,
    cycle,
  }
}

export const isBillingUpgrade = (
  currentPlan,
  newPlan,
  currentBillingCycle,
  newBillingCycle
) => {
  if (currentBillingCycle === 'annual' && newBillingCycle === 'monthly')
    return false
  if (newPlan.monthlyPrice < currentPlan.monthlyPrice) return false

  return true
}

export const showKbPlans = (kbPlan, accountKbBillingVersion = null) => {
  if (accountKbBillingVersion && kbPlan?.version !== accountKbBillingVersion)
    return true

  return kbPlan?.version !== 'v5'
}

export const calculatePerSecondRate = (cycleMonthlyPrice, month) => {
  // Define the number of days in each month.
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

  // Check for leap year for February.
  const now = new Date()
  const year = now.getFullYear()
  const leapYearCheck = new Date(year, 1, 29).getMonth() === 1 // February is month 1 in JavaScript date
  if (leapYearCheck) {
    daysInMonth[1] = 29
  }

  // Calculate the number of seconds in the month.
  const secondsInMonth = daysInMonth[month] * 24 * 60 * 60

  // Calculate the per second price.
  const perSecondPrice = cycleMonthlyPrice / secondsInMonth

  return perSecondPrice
}

export const calculateProrataAmount = (
  cycleMonthlyPrice,
  startTimestamp,
  endTimestamp
) => {
  let totalCharge = 0
  let currentTimestamp = startTimestamp

  // Loop through each month in the period.
  while (currentTimestamp < endTimestamp) {
    const currentDate = new Date(currentTimestamp)
    const currentMonth = currentDate.getMonth()
    const nextMonthTimestamp = new Date(
      currentDate.getFullYear(),
      currentMonth + 1,
      1
    ).getTime()

    let periodEndTimestamp
    // Check if the period ends in the current month or in a later month.
    if (nextMonthTimestamp > endTimestamp) {
      periodEndTimestamp = endTimestamp
    } else {
      periodEndTimestamp = nextMonthTimestamp
    }

    // Calculate the number of seconds in this part of the period.
    const secondsInPeriod = (periodEndTimestamp - currentTimestamp) / 1000

    // Calculate the charge for this part of the period.
    const perSecondRate = calculatePerSecondRate(
      cycleMonthlyPrice,
      currentMonth
    )

    totalCharge += perSecondRate * secondsInPeriod

    // Move to the next month.
    currentTimestamp = nextMonthTimestamp
  }

  return totalCharge
}

export const getPastTimestamp = (timestamp, period) => {
  const date = new Date(timestamp)

  switch (period) {
    case 'monthly':
      date.setMonth(date.getMonth() - 1)
      break
    case 'annual':
      date.setFullYear(date.getFullYear() - 1)
      break
    default:
      // eslint-disable-next-line no-console
      console.error('Invalid period')
      return null
  }

  return date.getTime()
}

export const getFutureTimestamp = (timestamp, period) => {
  const date = new Date(timestamp)

  switch (period) {
    case 'monthly':
      date.setMonth(date.getMonth() + 1)
      break
    case 'annual':
      date.setFullYear(date.getFullYear() + 1)
      break
    default:
      // eslint-disable-next-line no-console
      console.error('Invalid period')
      return null
  }

  return date.getTime()
}
